import { Check } from "lucide-react";
import { FundMark, coverStyle, type CoverBackground } from "@/pages/raise-board/rb-ui";
import { cn } from "@/lib/utils";

interface SubmissionHeaderProps {
  activeStep: number;
  totalSteps?: number;
  completedSteps?: Set<number>;
  onGoToStep?: (step: number) => void;
  /** The fund being applied to, so the founder always knows whose form this is. */
  fundName?: string;
  logoUrl?: string | null;
  /** Back to the fund's portal page. */
  homeHref?: string;
  /** Step names; the portfolio-update path renames the last one. */
  labels?: string[];
  /** The fund's header colour; the bar carries the colour only, no image. */
  background?: CoverBackground | null;
}

const DEFAULT_LABELS = ["About you", "Company", "Documents", "Application"];

/**
 * The submission flow's top bar: whose application this is on the left, the
 * steps on the right, and a hairline of progress along the bottom edge.
 *
 * A solid ink bar, continuous with the step's ink band below it. The current
 * step is a filled paper circle, done steps carry a check, the rest are
 * quiet; there is no colour-coding to decode.
 */
function SubmissionHeader({
  activeStep,
  totalSteps = 4,
  completedSteps = new Set(),
  onGoToStep,
  fundName,
  logoUrl,
  homeHref,
  labels = DEFAULT_LABELS,
  background,
}: SubmissionHeaderProps) {
  const isStepClickable = (step: number) =>
    completedSteps.has(step) && step < activeStep && !!onGoToStep;

  const identity = (
    <>
      <FundMark
        name={fundName}
        logoUrl={logoUrl}
        className="border-transparent bg-[hsl(220_30%_96%)] text-[hsl(226_34%_8%)]"
      />
      <span className="truncate text-sm font-semibold tracking-tight">
        {fundName ?? "Application"}
      </span>
    </>
  );

  return (
    <header
      className="ink-scope sticky top-0 z-40 border-b border-border/60 bg-background"
      style={background?.color ? coverStyle({ color: background.color }) : undefined}
    >
      <div className="mx-auto flex h-14 w-full max-w-5xl items-center gap-4 px-5 sm:px-8">
        {homeHref ? (
          <a
            href={homeHref}
            className="flex min-w-0 items-center gap-2.5 rounded-md transition-opacity hover:opacity-80"
          >
            {identity}
          </a>
        ) : (
          <span className="flex min-w-0 items-center gap-2.5">{identity}</span>
        )}

        <ol className="ml-auto hidden items-center gap-1 md:flex" aria-label="Application steps">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step, index) => {
            const isCompleted = step < activeStep;
            const isActive = step === activeStep;
            const clickable = isStepClickable(step);
            const label = labels[index] ?? `Step ${step}`;

            const content = (
              <>
                <span
                  className={cn(
                    "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums transition-colors duration-200",
                    isActive
                      ? "bg-foreground text-background"
                      : isCompleted
                        ? "border border-foreground/40 text-foreground"
                        : "border border-border text-muted-foreground"
                  )}
                >
                  {isCompleted ? <Check className="h-3 w-3" strokeWidth={3} /> : step}
                </span>
                <span
                  className={cn(
                    "whitespace-nowrap text-[13px] transition-colors duration-200",
                    isActive
                      ? "font-medium text-foreground"
                      : isCompleted
                        ? "text-foreground/75"
                        : "text-muted-foreground"
                  )}
                >
                  {label}
                </span>
              </>
            );

            return (
              <li key={step} className="flex items-center gap-1">
                {clickable ? (
                  <button
                    type="button"
                    onClick={() => onGoToStep!(step)}
                    title={`Go back to ${label}`}
                    className="flex items-center gap-2 rounded-md px-2 py-1 transition-colors duration-150 hover:bg-accent"
                  >
                    {content}
                  </button>
                ) : (
                  <span
                    className="flex items-center gap-2 px-2 py-1"
                    aria-current={isActive ? "step" : undefined}
                  >
                    {content}
                  </span>
                )}
                {index < totalSteps - 1 && (
                  <span aria-hidden className="h-px w-5 bg-border" />
                )}
              </li>
            );
          })}
        </ol>

        <p className="ml-auto shrink-0 text-[13px] tabular-nums text-muted-foreground md:hidden">
          Step {activeStep} of {totalSteps}
        </p>
      </div>

      {/* Progress along the bottom edge, drawn over the border. */}
      <div aria-hidden className="absolute inset-x-0 -bottom-px h-px">
        <div
          className="h-px w-full origin-left bg-foreground transition-transform duration-500 ease-out"
          style={{ transform: `scaleX(${Math.min(activeStep / totalSteps, 1)})` }}
        />
      </div>
    </header>
  );
}

export default SubmissionHeader;
